<template>
    <v-container class="pa-5" fluid>
        <PlateOrderItemsDialog
            :plate-order-data="plateOrder"
            :is-open="plateOrderItemsDialog"
            @get-plate-orders="getPlateOrders"
            @on-close="closePlateOrderItemsDialog"
        />
        <ConfirmDialog
            :is-open="turnSignalOffDialog"
            @on-confirm="turnSignalOff()"
            @on-abort="closeTurnSignalOffDialog"
        >
            <template #title>
                Wyłączanie alarmu
            </template>
            <template #content>
                Czy na pewno chcesz wyłączyć alarm?
            </template>
            <template #confirmButton>
                Potwierdź
            </template>
        </ConfirmDialog>

        <page-title>Lista zamówień</page-title>
        <v-row align="center">
            <v-col v-if="canTurnSignallerOff" cols="12" sm="4">
                <v-btn
                    color="secondary"
                    medium
                    data-sentry-label="Wyłącz alarm"
                    @click="showTurnSignalOffDialog()"
                >
                    Wyłącz alarm
                </v-btn>
            </v-col>
            <v-col cols="12" :sm="canTurnSignallerOff ? 8 : 12">
                <v-container fluid class="pa-0">
                    <v-row dense justify="end">
                        <v-col sm="auto">
                            <v-checkbox
                                v-model="filters.status"
                                label="Ukryj wykonane"
                                hide-details
                                class="mt-2"
                                @change="changeFilter"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="plateOrders"
                        :loading="plateOrdersLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :mobile-breakpoint="0"
                        name="plateOrdersData"
                        @pagination="updatePagination"
                    >
                        <template #item.jobNumber="{item}">
                            {{ item.jobNumber }}
                        </template>
                        <template #item.plateNumber="{item}">
                            {{ item.plateNumber }}
                        </template>
                        <template #item.symbol="{item}">
                            <template v-if="item.symbol">
                                {{ item.symbol }}
                            </template>
                            <template v-else>
                                -
                            </template>
                        </template>
                        <template #item.createdBy="{item}">
                            {{ item.createdByFirstName }} {{ item.createdByLastName }}
                        </template>
                        <template #item.createdAt="{item}">
                            {{ formatDate(item.createdAt) }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Otwórz"
                                            v-on="on"
                                            @click="showPlateOrderItemsDialog(item.id)"
                                        >
                                            <v-icon color="blue">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Otwórz</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import { errorHandler } from '@/utils/errorHandler'
import dateFormatter from '@/mixins/dateFormatter'
import customFilters from '@/mixins/customFilters'
import PlateOrderItemsDialog from './PlateOrderItemsDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import { hasUserPermission } from '@/utils/permissionsHandler'

export default {
    name: 'PlateOrdersList',
    components: {
        PageTitle,
        PlateOrderItemsDialog,
        ConfirmDialog
    },
    mixins: [
        pagination,
        tooltipDelay,
        filters,
        dateFormatter,
        customFilters
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('prepress/clearPlateOrdersFilters')
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            plateOrder: {},
            plateOrderItemsDialog: false,
            turnSignalOffDialog: false,
            headers: [
                {
                    text: 'Numer zlecenia',
                    value: 'jobNumber',
                    sortable: false,
                },
                {
                    text: 'Montaż',
                    value: 'plateNumber',
                    sortable: false,
                },
                {
                    text: 'Symbol maszyny',
                    value: 'symbol',
                    sortable: false,
                },
                {
                    text: 'Data utworzenia',
                    value: 'createdAt',
                    sortable: false,
                },
                {
                    text: 'Utworzone przez',
                    value: 'createdBy',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        plateOrders() {
            return this.$store.state.prepress.plateOrders.plateOrders
        },
        plateOrdersLoading() {
            return this.$store.state.prepress.plateOrders.plateOrdersLoading
        },
        savedFilters() {
            return this.$store.state.prepress.plateOrders.filters
        },
        canTurnSignallerOff() {
            return hasUserPermission('plate_orders_create')
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    if (item === 'status') {
                        filters[item] = 10
                    }
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        }
    },
    mounted() {
        this.getPlateOrders()
    },
    methods: {
        getPlateOrders() {
            this.$store.dispatch('prepress/getPlateOrders', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        async getPlateOrder(plateOrderId) {
            return await this.$store.dispatch('prepress/getPlateOrder', plateOrderId)
                .then(response => {
                    this.plateOrder = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showPlateOrderItemsDialog(plateOrderId) {
            this.getPlateOrder(plateOrderId)
                .then(() => {
                    this.plateOrderItemsDialog = true
                })
        },
        closePlateOrderItemsDialog() {
            this.plateOrderItemsDialog = false
            setTimeout(() => {
                this.plateOrder = {}
            }, 500)
        },
        showTurnSignalOffDialog() {
            this.turnSignalOffDialog = true
        },
        closeTurnSignalOffDialog() {
            this.turnSignalOffDialog = false
        },
        turnSignalOff() {
            this.actionLoading = true
            this.$store.dispatch('prepress/turnSignalOff')
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeTurnSignalOffDialog()
                })
        },
        changeFilter() {
            this.$store.dispatch('prepress/setPlateOrdersFilters', this.filters)
            this.resetPagination()
            this.getPlateOrders()
        },
        updatePagination(pagination) {
            this.pagination = pagination

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getPlateOrders()
            }
            this.clearPaginationPending()
        }
    }
}
</script>
